import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, ArrowLeft, Spinner, StopCircle } from '@phosphor-icons/react';

export default function QuickReportPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const institutionId = location.state?.institutionId;

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const hasStartedRef = useRef(false);
  const uploadedRef = useRef(false);
  const countdownRef = useRef(30);

  const [countdown, setCountdown] = useState(30);
  const [phase, setPhase] = useState('init'); // init, recording, uploading, success, error
  const [reportNo, setReportNo] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const API = '/api';

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const uploadVideo = useCallback(async (blob) => {
    // Prevent duplicate uploads
    if (uploadedRef.current) return;
    uploadedRef.current = true;

    setPhase('uploading');
    const formData = new FormData();
    formData.append('video', blob, 'incident.webm');
    formData.append('institution_id', institutionId);
    formData.append('video_duration', String(30 - countdownRef.current));
    formData.append('device_info', navigator.userAgent);
    formData.append('browser_info', navigator.userAgent);

    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        formData.append('latitude', String(pos.coords.latitude));
        formData.append('longitude', String(pos.coords.longitude));
        formData.append('location_accuracy', String(pos.coords.accuracy));
      } catch {
        // Location not available
      }
    }

    try {
      const res = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setReportNo(data.report_no);
      setPhase('success');
      toast.success('İhbar başarıyla gönderildi!');
    } catch (err) {
      setErrorMsg('Video yüklenirken hata oluştu. Tekrar deneyin.');
      setPhase('error');
      toast.error('Yükleme başarısız');
    }
  }, [institutionId, token, API]);

  const startRecording = useCallback(async () => {
    // Prevent double initialization from React StrictMode
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Use play() with catch to avoid "interrupted" errors
        try { await videoRef.current.play(); } catch { /* autoPlay handles it */ }
      }

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mimeType });
        uploadVideo(blob);
      };

      recorder.start(1000);
      setPhase('recording');
      setCountdown(30);

      let seconds = 30;
      timerRef.current = setInterval(() => {
        seconds -= 1;
        setCountdown(seconds);
        countdownRef.current = seconds;
        if (seconds <= 0) {
          clearInterval(timerRef.current);
          if (recorder.state === 'recording') recorder.stop();
        }
      }, 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setErrorMsg('Kamera izni reddedildi. Tarayıcı ayarlarından izin verin.');
      } else if (err.name === 'NotFoundError') {
        setErrorMsg('Kamera bulunamadı. Cihazınızı kontrol edin.');
      } else {
        setErrorMsg('Kamera başlatılırken hata oluştu.');
      }
      setPhase('error');
    }
  }, [stopStream, uploadVideo]);

  useEffect(() => {
    if (!institutionId) { navigate('/reporter'); return; }
    startRecording();

    return () => {
      clearInterval(timerRef.current);
      // On StrictMode cleanup, stop recorder without triggering upload
      if (mediaRecorderRef.current?.state === 'recording') {
        // Remove onstop to prevent upload during cleanup
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
      stopStream();
      // Do NOT reset hasStartedRef here - we want to prevent re-init
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Success Screen
  if (phase === 'success') {
    return (
      <div className="h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-8 animate-slide-up">
        <CheckCircle weight="fill" className="w-24 h-24 text-[hsl(var(--success))] mb-6" />
        <h1 className="font-display text-3xl text-white mb-2">İHBAR GÖNDERİLDİ</h1>
        <p className="overline text-[hsl(var(--muted-foreground))] mb-4">İhbar Numarası</p>
        <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-lg px-6 py-4 mb-8">
          <p data-testid="report-number" className="font-display text-2xl text-[hsl(var(--accent))]">{reportNo}</p>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center mb-8 max-w-xs">
          İhbarınız ilgili kuruma iletildi. Durumunu "İhbarlarım" sayfasından takip edebilirsiniz.
        </p>
        <div className="flex gap-3">
          <Button
            data-testid="back-to-dashboard-button"
            onClick={() => navigate('/reporter')}
            className="h-14 px-6 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading uppercase"
          >
            Yeni İhbar
          </Button>
          <Button
            data-testid="view-reports-button"
            onClick={() => navigate('/reporter/reports')}
            variant="outline"
            className="h-14 px-6 border-[#2C2C2E] text-white hover:bg-[#2C2C2E] hover:text-white font-heading uppercase"
          >
            İhbarlarım
          </Button>
        </div>
      </div>
    );
  }

  // Error Screen
  if (phase === 'error') {
    return (
      <div className="h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-8 animate-slide-up">
        <XCircle weight="fill" className="w-24 h-24 text-[hsl(var(--primary))] mb-6" />
        <h1 className="font-display text-3xl text-white mb-4">HATA</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] text-center mb-8 max-w-xs">{errorMsg}</p>
        <div className="flex gap-3">
          <Button
            data-testid="retry-button"
            onClick={() => { hasStartedRef.current = false; uploadedRef.current = false; setPhase('init'); startRecording(); }}
            className="h-14 px-6 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading uppercase"
          >
            Tekrar Dene
          </Button>
          <Button
            data-testid="go-back-button"
            onClick={() => navigate('/reporter')}
            variant="outline"
            className="h-14 px-6 border-[#2C2C2E] text-white hover:bg-[#2C2C2E] hover:text-white font-heading uppercase"
          >
            Geri Dön
          </Button>
        </div>
      </div>
    );
  }

  // Uploading Screen
  if (phase === 'uploading') {
    return (
      <div className="h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-8">
        <Spinner weight="bold" className="w-16 h-16 text-[hsl(var(--primary))] animate-spin mb-6" />
        <h1 className="font-display text-3xl text-white mb-2">YÜKLENİYOR</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Video gönderiliyor, lütfen bekleyin...</p>
      </div>
    );
  }

  // Recording Screen (and init)
  return (
    <div className="h-screen w-screen bg-black relative overflow-hidden">
      {/* Camera Preview */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Dark overlay at top and bottom */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent z-10" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent z-10" />

      {/* Back button */}
      <button
        data-testid="back-button"
        onClick={() => navigate('/reporter')}
        className="absolute top-4 left-4 z-20 w-12 h-12 rounded-full bg-black/50 flex items-center justify-center"
      >
        <ArrowLeft weight="bold" className="w-6 h-6 text-white" />
      </button>

      {/* Countdown Timer */}
      {phase === 'recording' && (
        <div className="absolute top-6 left-0 right-0 z-20 flex flex-col items-center">
          <div className="countdown-tick">
            <span
              data-testid="countdown-timer"
              className="font-display text-7xl text-white camera-text-shadow"
              aria-live="assertive"
            >
              {countdown}
            </span>
          </div>
          <p className="overline text-white/80 camera-text-shadow mt-1">KAYIT DEVAM EDİYOR</p>
        </div>
      )}

      {/* Stop & Send button */}
      {phase === 'recording' && (
        <div className="absolute bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-3">
          <button
            data-testid="stop-record-button"
            onClick={() => {
              clearInterval(timerRef.current);
              if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
            }}
            className="w-20 h-20 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center record-button-pulse active:scale-90 transition-transform"
          >
            <StopCircle weight="fill" className="w-10 h-10 text-white" />
          </button>
          <span className="text-white font-heading uppercase text-xs camera-text-shadow">Kaydı Bitir ve Gönder</span>
        </div>
      )}

      {/* Init loading */}
      {phase === 'init' && (
        <div className="absolute inset-0 z-30 bg-black flex items-center justify-center">
          <div className="text-center">
            <Spinner weight="bold" className="w-12 h-12 text-[hsl(var(--primary))] animate-spin mx-auto mb-4" />
            <p className="text-white font-heading">KAMERA HAZIRLANIYOR...</p>
          </div>
        </div>
      )}
    </div>
  );
}
